/**
 * bootProgress - the weighted-stage progress model behind the first-boot
 * loading screen (src/ui/bootScreen.js).
 *
 * The boot pipeline reports per-stage fractions (engine creation, scene
 * setup, level asset loads, shader warmup, finalize) and this module folds them into one
 * monotonic overall fraction, broadcast as a window `oggi:boot-progress`
 * CustomEvent. Emitters stay boot-agnostic: FuturisticExhibitionLevel just
 * dispatches `oggi:level-load-progress` {done,total} for its parallel asset
 * loads on EVERY load, and the bridge installed here maps that onto the
 * assets stage - after completeBoot() all reports are ignored, so later
 * in-world level switches emitting the same event are harmless.
 */

/**
 * Stage weights - must sum to 1. Assets now cover only the spawn-critical
 * manifest, shell, and drone; shader warmup owns a larger wall-clock share.
 * @type {Readonly<Record<string, number>>}
 */
export const BOOT_STAGES = Object.freeze({
  engine: 0.1,
  scene: 0.15,
  assets: 0.35,
  shaders: 0.25,
  finalize: 0.15,
});

/** @type {Record<string, number>} */
let fractions = { engine: 0, scene: 0, assets: 0, shaders: 0, finalize: 0 };
let completed = false;
let lastOverall = 0;
let bridgeInstalled = false;

/** @param {number} v */
function clamp01(v) {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;
}

/**
 * Pure: weighted overall progress from per-stage fractions.
 * @param {Record<string, number>} stageFractions - 0..1 per BOOT_STAGES key
 * @returns {number} 0..1
 */
export function computeOverall(stageFractions) {
  let sum = 0;
  for (const [stage, weight] of Object.entries(BOOT_STAGES)) {
    sum += weight * clamp01(stageFractions[stage] ?? 0);
  }
  return sum;
}

/**
 * @param {string} stage
 * @param {number} overall
 */
function dispatch(stage, overall) {
  try {
    window.dispatchEvent(new CustomEvent('oggi:boot-progress', { detail: { stage, overall } }));
  } catch {
    /* no window (tests) or dispatch failure - progress is cosmetic */
  }
}

/**
 * Report progress for one boot stage. Per-stage fractions and the overall
 * value are both monotonic (a stage can never regress the bar), and every
 * report is a no-op once completeBoot() has run.
 * @param {string} stage - a BOOT_STAGES key
 * @param {number} fraction - 0..1 completion of that stage
 */
export function reportBootStage(stage, fraction) {
  if (completed || !(stage in BOOT_STAGES)) return;
  fractions[stage] = Math.max(fractions[stage], clamp01(fraction));
  lastOverall = Math.max(lastOverall, computeOverall(fractions));
  dispatch(stage, lastOverall);
}

/**
 * Mark the boot finished: overall snaps to 1, a final event fires, and all
 * later reports (e.g. asset progress from in-world level switches) are
 * ignored.
 */
export function completeBoot() {
  if (completed) return;
  completed = true;
  lastOverall = 1;
  dispatch('finalize', 1);
}

/** @returns {boolean} */
export function bootCompleted() {
  return completed;
}

/**
 * Bridge level asset progress onto the assets stage. Installed once by
 * loginFlow before the boot starts; listens for `oggi:level-load-progress`
 * {done,total} dispatched by the level's parallel asset loads.
 */
export function initBootProgressBridge() {
  if (bridgeInstalled) return;
  bridgeInstalled = true;
  window.addEventListener('oggi:level-load-progress', (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail || {};
    const { done, total } = detail;
    if (typeof done === 'number' && typeof total === 'number' && total > 0) {
      reportBootStage('assets', done / total);
    }
  });
}

/** Test-only: reset module state (the bridge listener is not removed). */
export function _resetForTests() {
  fractions = { engine: 0, scene: 0, assets: 0, shaders: 0, finalize: 0 };
  completed = false;
  lastOverall = 0;
}
